import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { hasPermission } from '../utils/permissionUtils';
import '../styles/manage-telegram-bot.css';

const ManageTelegramBot = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [botInfo, setBotInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [updating, setUpdating] = useState(false);
  
  // Form states
  const [messageForm, setMessageForm] = useState({
    chatId: '',
    text: '',
    parseMode: 'HTML',
    topicId: ''
  });
  const [webhookForm, setWebhookForm] = useState({
    url: ''
  });
  const [customContext, setCustomContext] = useState('');
  const [contextLoading, setContextLoading] = useState(false);
  const [greetingConfig, setGreetingConfig] = useState('');
  const [greetingLoading, setGreetingLoading] = useState(false);
  const [toast, setToast] = useState(null);

  // Test mode states
  const [testMode, setTestMode] = useState({
    enabled: false,
    testChatId: ''
  });
  const [testModeLoading, setTestModeLoading] = useState(false);

  // Topic configuration states
  const [topicConfigs, setTopicConfigs] = useState([]);
  const [topicConfigsLoading, setTopicConfigsLoading] = useState(false);

  // Director reminder states
  const [directorReminder, setDirectorReminder] = useState({
    enabled: false,
    director_telegram_id: '',
    message_text: 'Hi! Don\'t forget to post rehearsal notes for today\'s practice. Thanks!',
    pre_rehearsal_enabled: false,
    hours_before_rehearsal: 4,
    pre_rehearsal_message: 'Hi! Just a reminder to add rehearsal notes for today\'s upcoming rehearsal. Thanks!'
  });
  const [directorReminderLoading, setDirectorReminderLoading] = useState(false);

  // Check permissions
  const canManageBot = hasPermission(user, 'manage-telegram-bot');

  useEffect(() => {
    if (isOpen && canManageBot) {
      loadBotInfo();
      loadBotContext();
      loadGreetingConfig();
      loadTestMode();
      loadTopicConfigs();
      loadDirectorReminder();
    }
  }, [isOpen, canManageBot]);

  const loadBotInfo = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram`);
      
      if (response.ok) {
        const data = await response.json();
        setBotInfo(data.bot);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || t('telegram.loadError'));
      }
    } catch (error) {
      console.error('Error loading bot info:', error);
      setError(t('telegram.errors.failedToLoadBotInfo'));
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'sendMessage',
          ...messageForm
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(t('telegram.success.messageSent'));
        setMessageForm({ chatId: '', text: '', parseMode: 'HTML', topicId: '' });
      } else {
        throw new Error(data.error || t('telegram.errors.failedToSendMessage'));
      }
    } catch (error) {
      console.error('Error sending message:', error);
      setError(t('telegram.errors.failedToSendMessage') + ': ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const setWebhook = async (e) => {
    e.preventDefault();
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'setWebhook',
          ...webhookForm
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccess(webhookForm.url ? t('telegram.success.webhookSet') : t('telegram.success.webhookRemoved'));
        setWebhookForm({ url: '' });
      } else {
        throw new Error(data.error || t('telegram.errors.failedToSetWebhook'));
      }
    } catch (error) {
      console.error('Error setting webhook:', error);
      setError(t('telegram.errors.failedToSetWebhook') + ': ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getUpdates = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
    let originalWebhookUrl = null;

    try {
      // Step 1: Get current webhook info to save it
      setSuccess('Checking current webhook configuration...');
      const webhookInfoResponse = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getWebhookInfo'
        }),
      });

      const webhookInfoData = await webhookInfoResponse.json();
      console.log('Webhook info response:', webhookInfoData);
      
      if (webhookInfoResponse.ok && webhookInfoData.success) {
        originalWebhookUrl = webhookInfoData.webhook.url || null;
        console.log('Current webhook URL:', originalWebhookUrl);
        console.log('Full webhook info:', webhookInfoData.webhook);
      } else {
        console.error('Failed to get webhook info:', webhookInfoData);
        throw new Error('Failed to get webhook info: ' + (webhookInfoData.error || 'Unknown error'));
      }

      // Step 2: Temporarily remove webhook if it exists
      if (originalWebhookUrl) {
        setSuccess('Temporarily removing webhook to get updates...');
        const removeWebhookResponse = await fetch(`${API_BASE}/api/telegram`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'setWebhook',
            url: ''
          }),
        });

        const removeWebhookData = await removeWebhookResponse.json();
        if (!removeWebhookResponse.ok || !removeWebhookData.success) {
          throw new Error('Failed to temporarily remove webhook');
        }
        
        // Wait a moment for webhook removal to take effect
        await new Promise(resolve => setTimeout(resolve, 1000));
      }

      // Step 3: Get updates
      setSuccess('Retrieving recent updates...');
      const response = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUpdates'
        }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Failed to get updates');
      }

      // Step 4: Restore webhook if it was originally set
      if (originalWebhookUrl) {
        setSuccess('Restoring webhook configuration...');
        const restoreWebhookResponse = await fetch(`${API_BASE}/api/telegram`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            action: 'setWebhook',
            url: originalWebhookUrl
          }),
        });

        const restoreWebhookData = await restoreWebhookResponse.json();
        if (!restoreWebhookResponse.ok || !restoreWebhookData.success) {
          console.warn('Warning: Failed to restore webhook. You may need to manually reset it to:', originalWebhookUrl);
          setError(`Updates retrieved but failed to restore webhook. Please manually reset webhook to: ${originalWebhookUrl}`);
        }
      }

      // Step 5: Display results
      console.log('Recent updates response:', data);
      console.log('Updates array:', data.updates);
      console.log('Updates length:', data.updates?.length);
      
      // Enhanced update display with chat ID extraction
      if (data.updates && data.updates.length > 0) {
        const chatIds = [...new Set(data.updates
          .map(update => update.message?.chat?.id || update.callback_query?.message?.chat?.id)
          .filter(id => id !== undefined)
        )];
        
        // Extract topic information from forum messages
        const topicMessages = data.updates
          .filter(update => update.message?.message_thread_id)
          .map(update => ({
            chatId: update.message.chat.id,
            topicId: update.message.message_thread_id,
            chatTitle: update.message.chat.title,
            messageText: update.message.text?.substring(0, 50) || '[No text]'
          }));
        
        let resultMessage = `✅ Retrieved ${data.updates.length} recent updates\n`;
        
        if (chatIds.length > 0) {
          resultMessage += `\n📋 Found Chat IDs:\n`;
          chatIds.forEach(chatId => {
            const chatInfo = data.updates.find(update => 
              (update.message?.chat?.id === chatId) || 
              (update.callback_query?.message?.chat?.id === chatId)
            );
            const chat = chatInfo?.message?.chat || chatInfo?.callback_query?.message?.chat;
            
            if (chat) {
              const chatType = chat.type === 'private' ? '👤' : 
                             chat.type === 'group' ? '👥' : 
                             chat.type === 'supergroup' ? '🏢' : 
                             chat.type === 'channel' ? '📢' : '❓';
              
              resultMessage += `${chatType} ${chatId} - ${chat.title || chat.first_name || 'Unnamed'}\n`;
            } else {
              resultMessage += `📱 ${chatId}\n`;
            }
          });
          resultMessage += `\n💡 Copy the chat ID you need for CHOIR_CHAT_ID`;
        }
        
        // Add topic information if found
        if (topicMessages.length > 0) {
          resultMessage += `\n\n🧵 Found Topic IDs (for forum groups):\n`;
          topicMessages.forEach(topic => {
            resultMessage += `📝 Topic ID: ${topic.topicId} in "${topic.chatTitle}"\n`;
            resultMessage += `   Chat ID: ${topic.chatId}\n`;
            resultMessage += `   Message: "${topic.messageText}"\n\n`;
          });
          resultMessage += `💡 Use these Topic IDs in your .env file:\n`;
          resultMessage += `TELEGRAM_REHEARSAL_TOPIC_ID=${topicMessages[0]?.topicId}\n`;
          resultMessage += `TELEGRAM_NEWS_TOPIC_ID=YOUR_NEWS_TOPIC_ID`;
        }
        resultMessage += `\n\nCheck console for full details.`;
        
        setSuccess(resultMessage);
      } else {
        setSuccess(`✅ Updates retrieved but no recent messages found.\n\n🔍 Why this happens:\n• Your webhook is active and consuming messages\n• No recent unprocessed messages available\n• This is normal behavior!\n\n💡 Use the GREEN method below instead:\n• Go to web.telegram.org\n• Navigate to your channel\n• Copy chat ID from the URL\n\n📊 Raw response logged to console for debugging.`);
      }

    } catch (error) {
      console.error('Error getting updates:', error);
      
      // Attempt to restore webhook even if there was an error
      if (originalWebhookUrl) {
        try {
          setSuccess('Error occurred, attempting to restore webhook...');
          await fetch(`${API_BASE}/api/telegram`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              action: 'setWebhook',
              url: originalWebhookUrl
            }),
          });
        } catch (restoreError) {
          console.error('Failed to restore webhook after error:', restoreError);
        }
      }
      
      setError('Failed to get updates: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const [channelUsername, setChannelUsername] = useState('');

  const findChatIdDirectly = async () => {
    if (!channelUsername.trim()) {
      setError('Please enter your channel username (like @danangchoir)');
      return;
    }

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/send-test-to-channel`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          channelUsername: channelUsername.trim()
        }),
      });

      const data = await response.json();
      
      console.log('Full API response:', data);
      console.log('Response status:', response.status);
      
      if (response.ok && data.success) {
        const chatIdDisplay = data.chatId || 'NOT FOUND';
        const successMessage = `🎉 SUCCESS! Found your chat ID!\n\n📋 Chat Details:\n• ID: ${chatIdDisplay}\n• Name: ${data.chatTitle || 'Unknown'}\n• Type: ${data.chatType || 'Unknown'}\n\n✅ COPY THIS CHAT ID: ${chatIdDisplay}\n\n✅ Set environment variable:\nCHOIR_CHAT_ID=${chatIdDisplay}\n\n✅ Check your channel - you should see a test message!`;
        console.log('Setting success message:', successMessage);
        setSuccess(successMessage);
      } else {
        console.error('API error response:', data);
        setError(`❌ Failed: ${data.error || 'Unknown error'}\n\nFull response logged to console.\n\nTips:\n• Make sure bot is admin in channel\n• Try with @ prefix: @yourchannel\n• Try without @ prefix: yourchannel`);
      }
    } catch (error) {
      console.error('Error finding chat ID:', error);
      setError('Network error: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const testNotificationSystem = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const testData = {
        changeInfo: {
          type: 'updated',
          rehearsal: {
            date: '2025-06-22',
            time: '19:00',
            location: {
              name: 'Test Location',
              address: 'Test Address'
            },
            notes: 'This is a test notification from the system'
          },
          previousRehearsal: {
            date: '2025-06-22',
            time: '18:00',
            location: {
              name: 'Old Location',
              address: 'Old Address'
            },
            notes: 'Old notes'
          }
        },
        chatId: '-1002096206242'
      };
      
      const response = await fetch(`${API_BASE}/api/notify-rehearsal-change`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(testData),
      });

      const data = await response.json();
      
      console.log('Test notification response:', data);
      
      if (response.ok && data.success) {
        setSuccess(`✅ Test notification sent successfully!\n\nCheck your channel for a test rehearsal update message.\n\nIf you see it, the notification system is working!`);
      } else {
        setError(`❌ Test failed: ${data.error || 'Unknown error'}\n\nResponse logged to console.`);
      }
    } catch (error) {
      console.error('Error testing notification:', error);
      setError('Network error: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const showChatIdInstructions = () => {
    setSuccess(`This will be replaced by the direct method above`);
  };

  const getRecentChats = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-recent-chats`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('Recent chats from database:', data);
        
        if (data.success && data.chats && data.chats.length > 0) {
          let resultMessage = `💬 Recent Chat IDs from Bot Conversations:\n\n`;
          
          data.chats.forEach(chat => {
            const chatType = chat.chat_id > 0 ? '👤' : '🏢';
            const lastSeen = new Date(chat.last_interaction).toLocaleDateString();
            resultMessage += `${chatType} ${chat.chat_id} - ${chat.chat_title || 'Private Chat'}\n`;
            resultMessage += `   Last seen: ${lastSeen} (${chat.message_count} messages)\n\n`;
          });
          
          resultMessage += `💡 Copy the chat ID you need for CHOIR_CHAT_ID\n`;
          resultMessage += `📊 Full details logged to console`;
          
          setSuccess(resultMessage);
        } else {
          setSuccess('💬 No recent chat history found.\n\n🔍 This means either:\n• Your bot hasn\'t received messages recently\n• Webhook is storing data elsewhere\n• Try the manual methods above');
        }
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to get recent chats');
      }
    } catch (error) {
      console.error('Error getting recent chats:', error);
      setError('Failed to get recent chats: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const [testChatIdValue, setTestChatIdValue] = useState('');

  const testChatIdFunction = async () => {
    if (!testChatIdValue.trim()) {
      setError('Please enter a chat ID to test');
      return;
    }

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/find-chat-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'send_test_message',
          chatId: testChatIdValue.trim()
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(`✅ Test message sent successfully!\n\nChat Details:\n• ID: ${data.chatInfo.id}\n• Type: ${data.chatInfo.type}\n• Name: ${data.chatInfo.title}\n\nIf you received the message, this chat ID works for notifications!`);
      } else {
        setError(`❌ Test failed: ${data.error}\n\nCommon issues:\n• Wrong chat ID format\n• Bot not added to channel\n• Bot lacks send permissions`);
      }
    } catch (error) {
      console.error('Error testing chat ID:', error);
      setError('Network error: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const getBotInstructions = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      const response = await fetch(`${API_BASE}/api/find-chat-id`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          method: 'get_bot_info'
        }),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setSuccess(`🤖 Bot: @${data.bot.username}\n\n📋 Steps to find chat ID:\n\n1. ${data.instructions.step1}\n2. ${data.instructions.step2}\n3. ${data.instructions.step3}\n4. ${data.instructions.step4}\n\nBot ID: ${data.bot.id}`);
      } else {
        setError(`Failed to get bot info: ${data.error}`);
      }
    } catch (error) {
      console.error('Error getting bot instructions:', error);
      setError('Network error: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const testDirectUpdates = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      
      setSuccess('Testing direct getUpdates call (this may fail with webhook conflict)...');
      
      const response = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getUpdates'
        }),
      });

      const data = await response.json();
      
      console.log('Direct getUpdates response:', data);
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        if (data.success && data.updates) {
          setSuccess(`✅ Direct call worked! Found ${data.updates.length} updates.\n\nCheck console for details.\n\nIf empty, try:\n1. Send a message to your bot RIGHT NOW\n2. Click this button again immediately`);
        } else {
          setError(`Unexpected response format: ${JSON.stringify(data)}`);
        }
      } else {
        const errorMessage = data.error || `HTTP ${response.status}`;
        if (errorMessage.includes('webhook') || errorMessage.includes('Conflict')) {
          setError(`❌ Webhook conflict confirmed: ${errorMessage}\n\nThis proves your webhook is active. Use the GREEN method below instead.`);
        } else {
          setError(`API Error: ${errorMessage}`);
        }
      }
    } catch (error) {
      console.error('Error testing direct updates:', error);
      setError('Network error: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const checkEnvironment = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/debug-env`);
      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Environment check:', data.environment);
        const env = data.environment;
        let message = `Environment Status:\n`;
        message += `✅ Telegram Token: ${env.TELEGRAM_BOT_TOKEN ? 'Present' : 'Missing'}\n`;
        message += `${env.OPENAI_API_KEY ? '✅' : '❌'} OpenAI Key: ${env.OPENAI_API_KEY ? 'Present' : 'Missing'}\n`;
        message += `✅ Database: ${env.DATABASE_URL ? 'Present' : 'Missing'}\n`;
        message += `Check console for details.`;
        setSuccess(message);
      } else {
        throw new Error(data.error || 'Failed to check environment');
      }
    } catch (error) {
      console.error('Error checking environment:', error);
      setError('Failed to check environment: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const checkRehearsalData = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/debug-rehearsals`);
      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Rehearsal debug data:', data.debug);
        const debug = data.debug;
        let message = `Rehearsal Data Check:\n`;
        message += `📊 Tables Found: ${debug.tablesFound.join(', ')}\n`;
        message += `📅 Schedules: ${debug.schedulesCount}\n`;
        message += `📍 Locations: ${debug.locationsCount}\n`;
        message += `🕐 Current Day: ${debug.currentWeekday} (0=Sun, 1=Mon, etc.)\n`;
        message += `Check console for full details.`;
        
        if (debug.schedulesCount === 0) {
          message += `\n\n⚠️ No rehearsal schedules found! You may need to add rehearsal data in the Rehearsal Manager.`;
        }
        
        setSuccess(message);
      } else {
        throw new Error(data.error || 'Failed to check rehearsal data');
      }
    } catch (error) {
      console.error('Error checking rehearsal data:', error);
      setError('Failed to check rehearsal data: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const checkWebhookInfo = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'getWebhookInfo'
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        console.log('Webhook info:', data.webhook);
        const webhook = data.webhook;
        let message = `Webhook Status:\n`;
        message += `🔗 URL: ${webhook.url || 'Not set'}\n`;
        message += `✅ Has custom cert: ${webhook.has_custom_certificate ? 'Yes' : 'No'}\n`;
        message += `📊 Pending updates: ${webhook.pending_update_count}\n`;
        if (webhook.last_error_date) {
          message += `❌ Last error: ${new Date(webhook.last_error_date * 1000).toLocaleString()}\n`;
          message += `📝 Error message: ${webhook.last_error_message}\n`;
        }
        message += `Check console for full details.`;
        
        setSuccess(message);
      } else {
        throw new Error(data.error || 'Failed to check webhook info');
      }
    } catch (error) {
      console.error('Error checking webhook info:', error);
      setError('Failed to check webhook info: ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const loadBotContext = async () => {
    setContextLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-context`);
      
      if (response.ok) {
        const data = await response.json();
        setCustomContext(data.context || '');
      } else {
        // If no context exists yet, that's okay
        setCustomContext('');
      }
    } catch (error) {
      console.error('Error loading bot context:', error);
      setCustomContext('');
    } finally {
      setContextLoading(false);
    }
  };

  const saveBotContext = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-context`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          context: customContext
        }),
      });

      if (response.ok) {
        setSuccess(t('telegram.saveSuccess'));
        showToast('✓ Bot context saved successfully!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save context');
      }
    } catch (error) {
      console.error('Error saving bot context:', error);
      setError(t('telegram.saveError'));
    } finally {
      setUpdating(false);
    }
  };

  const loadGreetingConfig = async () => {
    setGreetingLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-greeting`);
      
      if (response.ok) {
        const data = await response.json();
        setGreetingConfig(data.greeting || '');
      } else {
        // If no greeting exists yet, that's okay
        setGreetingConfig('');
      }
    } catch (error) {
      console.error('Error loading greeting config:', error);
      setGreetingConfig('');
    } finally {
      setGreetingLoading(false);
    }
  };

  const saveGreetingConfig = async () => {
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-greeting`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          greeting: greetingConfig
        }),
      });

      if (response.ok) {
        setSuccess(t('telegram.saveSuccess'));
        showToast('✓ Greeting configuration saved!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save greeting config');
      }
    } catch (error) {
      console.error('Error saving greeting config:', error);
      setError(t('telegram.saveError'));
    } finally {
      setUpdating(false);
    }
  };

  const loadTestMode = async () => {
    setTestModeLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-test-mode`);
      
      if (response.ok) {
        const data = await response.json();
        setTestMode({
          enabled: data.enabled || false,
          testChatId: data.testChatId || ''
        });
      } else {
        // If no test mode config exists yet, that's okay
        setTestMode({ enabled: false, testChatId: '' });
      }
    } catch (error) {
      console.error('Error loading test mode config:', error);
      setTestMode({ enabled: false, testChatId: '' });
    } finally {
      setTestModeLoading(false);
    }
  };

  const saveTestMode = async () => {
    // Validate inputs
    if (testMode.enabled && !testMode.testChatId.trim()) {
      setError(t('telegram.testMode.testModeRequired'));
      return;
    }

    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-test-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          enabled: testMode.enabled,
          testChatId: testMode.testChatId.trim()
        }),
      });

      if (response.ok) {
        setSuccess(t('telegram.testMode.testModeSaved'));
        showToast(testMode.enabled ? '🧪 Test mode enabled!' : '📢 Test mode disabled!', 'success');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to save test mode config');
      }
    } catch (error) {
      console.error('Error saving test mode config:', error);
      setError(t('telegram.testMode.testModeFailed'));
    } finally {
      setUpdating(false);
    }
  };

  // Topic configuration functions
  const loadTopicConfigs = async () => {
    setTopicConfigsLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-topics?operation=getTopics`);
      
      if (response.ok) {
        const data = await response.json();
        setTopicConfigs(data);
      } else {
        console.error('Failed to load topic configurations');
      }
    } catch (error) {
      console.error('Error loading topic configurations:', error);
    } finally {
      setTopicConfigsLoading(false);
    }
  };

  const updateTopicConfig = async (category, topicId) => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-topics?operation=updateTopic`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          category,
          topic_id: topicId
        }),
      });

      if (response.ok) {
        await loadTopicConfigs(); // Reload to get updated data
        showToast(`${category} topic updated!`, 'success');
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to update topic configuration');
      }
    } catch (error) {
      console.error('Error updating topic configuration:', error);
      setError('Failed to update topic configuration');
    }
  };

  const loadDirectorReminder = async () => {
    setDirectorReminderLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-director-reminder`);
      
      if (response.ok) {
        const data = await response.json();
        setDirectorReminder(data);
      } else {
        console.error('Failed to load director reminder configuration');
      }
    } catch (error) {
      console.error('Error loading director reminder configuration:', error);
    } finally {
      setDirectorReminderLoading(false);
    }
  };

  const saveDirectorReminder = async () => {
    setDirectorReminderLoading(true);
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-director-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(directorReminder),
      });

      if (response.ok) {
        showToast(t('telegram.directorReminders.configUpdated'), 'success');
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('telegram.directorReminders.configUpdateFailed'));
      }
    } catch (error) {
      console.error('Error saving director reminder configuration:', error);
      setError(t('telegram.directorReminders.configUpdateFailed'));
    } finally {
      setDirectorReminderLoading(false);
    }
  };

  const sendTestDirectorReminder = async () => {
    try {
      setUpdating(true);
      
      // Simple API call to send test reminder
      const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
      const response = await fetch(`${API_BASE}/api/telegram-director-reminder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...directorReminder,
          test: true // Add test flag
        }),
      });

      if (response.ok) {
        showToast(t('telegram.directorReminders.testReminderSent'), 'success');
      } else {
        const errorData = await response.json();
        setError(errorData.error || t('telegram.directorReminders.testReminderFailed'));
      }
    } catch (error) {
      console.error('Error sending test reminder:', error);
      setError(t('telegram.directorReminders.testReminderFailed') + ': ' + error.message);
    } finally {
      setUpdating(false);
    }
  };

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  if (!isOpen) return null;

  if (!canManageBot) {
    return (
      <div className="manage-telegram-bot-overlay">
        <div className="manage-telegram-bot-dialog">
          <div className="manage-telegram-bot-header">
            <h2>{t('telegram.accessDenied')}</h2>
            <button className="close-button" onClick={onClose}>×</button>
          </div>
          <div className="manage-telegram-bot-content">
            <p>{t('telegram.noPermission')}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="manage-telegram-bot-overlay">
      <div className="manage-telegram-bot-dialog">
        <div className="manage-telegram-bot-header">
          <div className="header-content">
            <div className="header-icon">🤖</div>
            <div className="header-text">
              <h2>{t('telegram.title')}</h2>
              <p>{t('telegram.description')}</p>
            </div>
          </div>
          <button className="close-button" onClick={onClose}>×</button>
        </div>

        <div className="manage-telegram-bot-content">
          {/* Error/Success Messages */}
          {error && (
            <div className="error-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
              </svg>
              {success}
            </div>
          )}

          {/* Custom Bot Knowledge - Moved to top */}
          <div className="bot-context-section">
            <h3 style={{ color: '#f1f5f9' }}>{t('telegram.customKnowledge')}</h3>
            <p style={{ color: '#cbd5e1', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Add custom information that the bot should know about your choir. This will be included in all AI responses.
              You can add FAQs, specific choir policies, member info, or any other details you want the bot to reference.
            </p>
            
            <div className="form-group">
              <label htmlFor="customContext">Bot Context & Knowledge:</label>
              <textarea
                id="customContext"
                value={customContext}
                onChange={(e) => setCustomContext(e.target.value)}
                placeholder="Example:
FAQ:
- What should I bring to rehearsal? Please bring your music folder, a water bottle, and a positive attitude!
- Where do we park? Free parking is available in the lot behind the building.
- What's our dress code for concerts? Smart casual - dark bottoms, light colored tops.

About Our Choir:
- Founded in 2020 by Director Sarah Johnson
- We focus on multicultural repertoire
- All skill levels welcome, no auditions required
- We perform 3 concerts per year

Policies:
- Please arrive 10 minutes early to rehearsal
- If you can't attend, text the section leaders
- Sheet music is provided, but feel free to bring your own"
                rows="12"
                style={{ fontFamily: 'monospace', lineHeight: '1.4' }}
              />
              <small>
                This information will be available to the bot in all conversations. 
                The bot can reference this context when answering questions naturally.
              </small>
            </div>

            <button 
              className="send-button"
              onClick={saveBotContext}
              disabled={updating || contextLoading}
              style={{ marginTop: '0.5rem' }}
            >
              {updating ? t('telegram.saving') : t('telegram.saveBotContext')}
            </button>

            {contextLoading && (
              <div style={{ color: '#cbd5e1', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                {t('telegram.loading')}
              </div>
            )}
          </div>

          {/* New User Greeting Configuration */}
          <div className="bot-greeting-section">
            <h3 style={{ color: '#f1f5f9' }}>👋 New User Greeting</h3>
            <p style={{ color: '#cbd5e1', fontSize: '0.875rem', marginBottom: '1rem' }}>
              Configure information to include in personalized greetings for new users. The bot will use GPT to craft 
              unique welcome messages containing this information, with automatic language detection based on the user's name.
            </p>
            
            <div className="form-group">
              <label htmlFor="greetingConfig">Greeting Information:</label>
              <textarea
                id="greetingConfig"
                value={greetingConfig}
                onChange={(e) => setGreetingConfig(e.target.value)}
                placeholder="Example:
Welcome to Danang International Choir! Here's what new members should know:

About Us:
- We're a multicultural choir welcoming all skill levels
- Founded in 2020, we perform 3 concerts per year
- No auditions required - just bring enthusiasm!

Getting Started:
- Rehearsals are Tuesdays 7-9 PM at Community Center
- Bring a water bottle and notebook
- Free parking available behind the building
- WhatsApp group link: [your-link]

Next Steps:
- Join our next rehearsal to meet everyone
- Check the practice materials in our shared drive
- Questions? Contact our director Sarah at sarah@email.com

We're excited to have you join our musical family!"
                rows="16"
                style={{ fontFamily: 'monospace', lineHeight: '1.4' }}
              />
              <small>
                This information will be crafted into personalized welcome messages by GPT. 
                Each greeting will be unique and adapted to the user's apparent language.
              </small>
            </div>

            <button 
              className="send-button"
              onClick={saveGreetingConfig}
              disabled={updating || greetingLoading}
              style={{ marginTop: '0.5rem' }}
            >
              {updating ? t('telegram.saving') : t('telegram.saveGreeting')}
            </button>

            {greetingLoading && (
              <div style={{ color: '#cbd5e1', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                {t('telegram.loading')}
              </div>
            )}
          </div>

          {/* Test Mode Configuration */}
          <div className="test-mode-section">
            <h3>{t('telegram.testMode.title')}</h3>
            <p>
              {t('telegram.testMode.description')}
            </p>
            
            {/* Current Status */}
            <div className={`status-indicator ${testMode.enabled ? 'active' : 'inactive'}`}>
              {testMode.enabled ? t('telegram.testMode.testModeActive') : t('telegram.testMode.testModeInactive')}
            </div>

            {/* Enable/Disable Toggle */}
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={testMode.enabled}
                  onChange={(e) => setTestMode(prev => ({ ...prev, enabled: e.target.checked }))}
                />
                <span className="checkbox-text">
                  {testMode.enabled ? t('telegram.testMode.enabled') : t('telegram.testMode.disabled')}
                </span>
              </label>
            </div>

            {/* Test Chat ID Input (only shown when enabled) */}
            {testMode.enabled && (
              <div className="form-group">
                <label htmlFor="testChatId">{t('telegram.testMode.testChatId')}</label>
                <input
                  type="text"
                  id="testChatId"
                  value={testMode.testChatId}
                  onChange={(e) => setTestMode(prev => ({ ...prev, testChatId: e.target.value }))}
                  placeholder={t('telegram.testMode.testChatIdPlaceholder')}
                />
                <small>
                  {t('telegram.testMode.testChatIdHelp')}
                </small>
              </div>
            )}

            {/* Save Button */}
            <button 
              className="send-button"
              onClick={saveTestMode}
              disabled={updating || testModeLoading}
            >
              {updating ? t('telegram.saving') : t('telegram.testMode.saveTestMode')}
            </button>

            {testModeLoading && (
              <div className="loading-text">
                {t('telegram.loading')}
              </div>
            )}
          </div>

          {/* Bot Information */}
          <div className="bot-info-section">
            <h3 style={{ color: '#f1f5f9' }}>Bot Information</h3>
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>{t('telegram.loading')}</p>
              </div>
            ) : botInfo ? (
              <div className="bot-info">
                <div className="info-item">
                  <label>Bot Name:</label>
                  <span>{botInfo.first_name}</span>
                </div>
                <div className="info-item">
                  <label>Username:</label>
                  <span>@{botInfo.username}</span>
                </div>
                <div className="info-item">
                  <label>ID:</label>
                  <span>{botInfo.id}</span>
                </div>
                <div className="info-item">
                  <label>Can Join Groups:</label>
                  <span>{botInfo.can_join_groups ? 'Yes' : 'No'}</span>
                </div>
                <div className="info-item">
                  <label>Can Read All Group Messages:</label>
                  <span>{botInfo.can_read_all_group_messages ? 'Yes' : 'No'}</span>
                </div>
              </div>
            ) : (
              <p>Failed to load bot information</p>
            )}
            <button 
              className="refresh-button" 
              onClick={loadBotInfo}
              disabled={loading}
            >
              Refresh Bot Info
            </button>
          </div>

          {/* Send Message */}
          <div className="send-message-section">
            <h3 style={{ color: '#f1f5f9' }}>Send Message</h3>
            <form onSubmit={sendMessage}>
              <div className="form-group">
                <label htmlFor="chatId">Chat ID:</label>
                <input
                  type="text"
                  id="chatId"
                  value={messageForm.chatId}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, chatId: e.target.value }))}
                  placeholder="Enter chat ID or @username"
                  required
                />
                <small>Use negative number for groups (e.g., -1001234567890)</small>
              </div>
              
              <div className="form-group">
                <label htmlFor="messageText">Message:</label>
                <textarea
                  id="messageText"
                  value={messageForm.text}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Enter your message..."
                  rows="4"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="parseMode">Parse Mode:</label>
                <select
                  id="parseMode"
                  value={messageForm.parseMode}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, parseMode: e.target.value }))}
                >
                  <option value="HTML">HTML</option>
                  <option value="Markdown">Markdown</option>
                  <option value="">None</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="topicId">Topic ID (Optional):</label>
                <input
                  type="text"
                  id="topicId"
                  value={messageForm.topicId}
                  onChange={(e) => setMessageForm(prev => ({ ...prev, topicId: e.target.value }))}
                  placeholder="Enter topic ID (e.g. 1080) for forum groups"
                />
                <small style={{ color: '#9ca3af', fontSize: '0.8rem', display: 'block', marginTop: '0.25rem' }}>
                  Leave empty to send to main chat. Use topic ID for forum group topics.
                </small>
              </div>

              <button 
                type="submit" 
                className="send-button"
                disabled={updating}
              >
                {updating ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          {/* Webhook Management */}
          <div className="webhook-section">
            <h3 style={{ color: '#f1f5f9' }}>Webhook Management</h3>
            <form onSubmit={setWebhook}>
              <div className="form-group">
                <label htmlFor="webhookUrl">Webhook URL:</label>
                <input
                  type="url"
                  id="webhookUrl"
                  value={webhookForm.url}
                  onChange={(e) => setWebhookForm(prev => ({ ...prev, url: e.target.value }))}
                  placeholder="https://your-app.vercel.app/api/telegram-webhook"
                />
                <small>Leave empty to remove webhook</small>
              </div>
              
              <button 
                type="submit" 
                className="webhook-button"
                disabled={updating}
              >
                {updating ? 'Setting...' : 'Set Webhook'}
              </button>
            </form>
          </div>

          {/* Chat ID Finder */}
          <div className="updates-section">
            <h3 style={{ color: '#f1f5f9' }}>Find Your Channel Chat ID</h3>
            <p>Enter your channel username to find the chat ID automatically.</p>
            
            <div style={{ 
              background: 'rgba(34, 197, 94, 0.1)', 
              border: '1px solid rgba(34, 197, 94, 0.3)', 
              borderRadius: '0.5rem', 
              padding: '1rem',
              marginBottom: '1rem'
            }}>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <label htmlFor="channelUsername" style={{ 
                    display: 'block', 
                    marginBottom: '0.25rem', 
                    fontSize: '0.9rem',
                    color: '#22c55e',
                    fontWeight: 'bold'
                  }}>
                    Channel Username:
                  </label>
                  <input
                    type="text"
                    id="channelUsername"
                    value={channelUsername}
                    onChange={(e) => setChannelUsername(e.target.value)}
                    placeholder="@danangchoir or danangchoir"
                    style={{
                      width: '100%',
                      padding: '0.5rem',
                      borderRadius: '0.375rem',
                      border: '1px solid #374151',
                      background: '#1f2937',
                      color: '#f9fafb',
                      fontSize: '1rem'
                    }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                    Enter your channel's username (with or without @)
                  </small>
                </div>
                <button 
                  className="updates-button"
                  onClick={findChatIdDirectly}
                  disabled={updating || !channelUsername.trim()}
                  style={{ 
                    background: channelUsername.trim() 
                      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.8), rgba(22, 163, 74, 0.8))'
                      : 'rgba(107, 114, 128, 0.5)',
                    minWidth: '120px',
                    height: 'fit-content'
                  }}
                >
                  {updating ? 'Finding...' : '🔍 Find Chat ID'}
                </button>
              </div>
              
              <div style={{ borderTop: '1px solid rgba(34, 197, 94, 0.3)', paddingTop: '1rem' }}>
                <h5 style={{ color: '#22c55e', margin: '0 0 0.5rem 0' }}>🧪 Test Notifications</h5>
                <button 
                  className="updates-button"
                  onClick={testNotificationSystem}
                  disabled={updating}
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.8), rgba(37, 99, 235, 0.8))',
                    minWidth: '120px'
                  }}
                >
                  {updating ? 'Testing...' : '🔔 Test Notification'}
                </button>
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  This will send a test rehearsal change notification to your channel
                </small>
                
                <button 
                  className="updates-button"
                  onClick={getUpdates}
                  disabled={updating}
                  style={{ 
                    background: 'linear-gradient(135deg, rgba(236, 72, 153, 0.8), rgba(219, 39, 119, 0.8))',
                    minWidth: '120px',
                    marginTop: '1rem'
                  }}
                >
                  {updating ? 'Getting...' : '🧵 Get Topic IDs'}
                </button>
                <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280', fontSize: '0.8rem' }}>
                  Find Topic IDs from recent messages in forum groups. Send messages to your topics first!
                </small>
              </div>
            </div>

            {/* Topic Configuration Section */}
            <div className="topic-config-section" style={{
              backgroundColor: '#1f2937',
              border: '1px solid rgba(147, 51, 234, 0.3)',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem'
            }}>
              <h3 style={{ color: '#a855f7', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                🧵 Topic Configuration
              </h3>
              <p style={{ color: '#9ca3af', fontSize: '0.9rem', marginBottom: '1rem' }}>
                Configure which Telegram topics receive different types of notifications. Leave empty to send to main chat.
              </p>
              
              {topicConfigsLoading ? (
                <div style={{ color: '#9ca3af', textAlign: 'center', padding: '1rem' }}>
                  Loading topic configurations...
                </div>
              ) : (
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {topicConfigs.map(config => (
                    <div key={config.category} style={{
                      backgroundColor: '#374151',
                      borderRadius: '8px',
                      padding: '1rem',
                      border: '1px solid #4b5563'
                    }}>
                      <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        marginBottom: '0.5rem'
                      }}>
                        <div>
                          <h4 style={{ 
                            color: '#f3f4f6', 
                            margin: 0, 
                            fontSize: '1rem',
                            textTransform: 'capitalize' 
                          }}>
                            {config.category === 'rehearsal' ? '🎭' : 
                             config.category === 'news' ? '📢' :
                             config.category === 'calendar' ? '📅' : '💬'} {config.category}
                          </h4>
                          <p style={{ 
                            color: '#9ca3af', 
                            margin: 0, 
                            fontSize: '0.8rem' 
                          }}>
                            {config.description}
                          </p>
                        </div>
                        <div style={{ 
                          backgroundColor: config.topic_id ? '#10b981' : '#6b7280',
                          color: 'white',
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75rem',
                          fontWeight: '600'
                        }}>
                          {config.topic_id ? `Topic: ${config.topic_id}` : 'Main Chat'}
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input
                          type="text"
                          placeholder="Enter topic ID (e.g. 1080)"
                          value={config.topic_id || ''}
                          onChange={(e) => {
                            const newConfigs = topicConfigs.map(c => 
                              c.category === config.category 
                                ? { ...c, topic_id: e.target.value }
                                : c
                            );
                            setTopicConfigs(newConfigs);
                          }}
                          style={{
                            flex: 1,
                            padding: '0.5rem',
                            backgroundColor: '#4b5563',
                            border: '1px solid #6b7280',
                            borderRadius: '4px',
                            color: '#f3f4f6',
                            fontSize: '0.9rem'
                          }}
                        />
                        <button
                          onClick={() => updateTopicConfig(config.category, config.topic_id)}
                          disabled={updating}
                          style={{
                            backgroundColor: '#a855f7',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '0.5rem 1rem',
                            fontSize: '0.8rem',
                            fontWeight: '600',
                            cursor: 'pointer',
                            opacity: updating ? 0.5 : 1
                          }}
                        >
                          {updating ? 'Saving...' : 'Save'}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            
            <button 
              className="updates-button"
              onClick={checkEnvironment}
              disabled={updating}
              style={{ marginLeft: '0.5rem', background: 'linear-gradient(135deg, rgba(168, 85, 247, 0.8), rgba(139, 92, 246, 0.8))' }}
            >
              Check Environment
            </button>
            
            <button 
              className="updates-button"
              onClick={checkRehearsalData}
              disabled={updating}
              style={{ marginLeft: '0.5rem', background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.8), rgba(22, 163, 74, 0.8))' }}
            >
              Check Rehearsal Data
            </button>
            
            <button 
              className="updates-button"
              onClick={checkWebhookInfo}
              disabled={updating}
              style={{ marginLeft: '0.5rem', background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.8), rgba(234, 88, 12, 0.8))' }}
            >
              Check Webhook Info
            </button>
          </div>

          {/* AI Configuration */}
          <div className="ai-config-section">
            <h3 style={{ color: '#f1f5f9' }}>🤖 ChatGPT Integration</h3>
            <div className="info-card">
              <h4>Setup Instructions</h4>
              <ol style={{ paddingLeft: '1rem', color: '#cbd5e1', fontSize: '0.875rem', lineHeight: '1.5' }}>
                <li>Set <strong>OPENAI_API_KEY</strong> in Vercel environment variables</li>
                <li>Set webhook URL to: <code style={{ background: 'rgba(59, 130, 246, 0.2)', padding: '0.125rem 0.25rem', borderRadius: '0.25rem' }}>https://your-app.vercel.app/api/telegram-webhook</code></li>
                <li>Add bot to your choir channel as admin</li>
                <li><strong>IMPORTANT:</strong> Disable "Group Privacy" in group settings so bot can see messages</li>
                <li>Test by mentioning the bot: <strong>@DanangChoirBot hello</strong></li>
              </ol>
              
              <div style={{ marginTop: '1rem', padding: '0.75rem', background: 'rgba(34, 197, 94, 0.1)', border: '1px solid rgba(34, 197, 94, 0.3)', borderRadius: '0.5rem' }}>
                <strong style={{ color: '#86efac' }}>💡 Pro Tip:</strong>
                <p style={{ margin: '0.25rem 0 0 0', color: '#cbd5e1', fontSize: '0.75rem' }}>
                  The bot will respond when mentioned in groups, or to any message in private chats. 
                  It has context about your choir and can answer questions about music, singing, and choir activities!
                </p>
              </div>
            </div>
          </div>


          {/* Director Rehearsal Reminders */}
          <div className="director-reminders-section">
            <h3 style={{ color: '#f1f5f9' }}>🎼 {t('telegram.directorReminders.title')}</h3>
            
            <div className="info-card">
              <h4>{t('telegram.directorReminders.automaticSystem')}</h4>
              <p style={{ color: '#cbd5e1', fontSize: '0.875rem', marginBottom: '1rem' }}>
                Send automatic reminders to the director after rehearsals end. The system checks hourly.
              </p>
              
              {directorReminderLoading ? (
                <div style={{ color: '#cbd5e1' }}>Loading configuration...</div>
              ) : (
                <div>
                  {/* Enable/Disable Toggle */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0' }}>
                      <input
                        type="checkbox"
                        checked={directorReminder.enabled}
                        onChange={(e) => setDirectorReminder(prev => ({ ...prev, enabled: e.target.checked }))}
                        style={{ width: '18px', height: '18px' }}
                      />
                      <strong>Enable Director Reminders</strong>
                    </label>
                  </div>

                  {/* Director Telegram ID */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                      <strong>Director Telegram ID:</strong>
                    </label>
                    <input
                      type="text"
                      value={directorReminder.director_telegram_id}
                      onChange={(e) => setDirectorReminder(prev => ({ ...prev, director_telegram_id: e.target.value }))}
                      placeholder="Enter director's Telegram user ID"
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '0.375rem',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        background: 'rgba(17, 24, 39, 0.8)',
                        color: '#f3f4f6',
                        fontSize: '0.875rem'
                      }}
                    />
                    <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                      <strong>Need your user ID?</strong> Send /start to @userinfobot on Telegram
                      <br />
                      Note: Use the numeric ID (like 123456789), not username (@andrewsilber)
                    </div>
                  </div>

                  {/* Message Text */}
                  <div style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                      <strong>Reminder Message:</strong>
                    </label>
                    <textarea
                      value={directorReminder.message_text}
                      onChange={(e) => setDirectorReminder(prev => ({ ...prev, message_text: e.target.value }))}
                      placeholder="Enter the reminder message to send"
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '0.5rem',
                        borderRadius: '0.375rem',
                        border: '1px solid rgba(75, 85, 99, 0.5)',
                        background: 'rgba(17, 24, 39, 0.8)',
                        color: '#f3f4f6',
                        fontSize: '0.875rem',
                        resize: 'vertical'
                      }}
                    />
                  </div>

                  {/* Pre-Rehearsal Reminder Section */}
                  <div style={{ 
                    marginTop: '1.5rem',
                    paddingTop: '1.5rem',
                    borderTop: '1px solid rgba(75, 85, 99, 0.5)'
                  }}>
                    <h5 style={{ color: '#e2e8f0', marginBottom: '0.75rem' }}>Pre-Rehearsal Reminder (Missing Notes)</h5>
                    
                    {/* Enable Pre-Rehearsal Reminder */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#e2e8f0' }}>
                        <input
                          type="checkbox"
                          checked={directorReminder.pre_rehearsal_enabled}
                          onChange={(e) => setDirectorReminder(prev => ({ ...prev, pre_rehearsal_enabled: e.target.checked }))}
                          style={{ width: '18px', height: '18px' }}
                        />
                        <strong>Enable Pre-Rehearsal Reminders</strong>
                      </label>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                        Send reminder if rehearsal notes are missing before the rehearsal
                      </div>
                    </div>

                    {/* Hours Before Rehearsal */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                        <strong>Send reminder X hours before rehearsal:</strong>
                      </label>
                      <input
                        type="number"
                        min="1"
                        max="24"
                        value={directorReminder.hours_before_rehearsal}
                        onChange={(e) => setDirectorReminder(prev => ({ ...prev, hours_before_rehearsal: parseInt(e.target.value) || 4 }))}
                        style={{
                          width: '100px',
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          border: '1px solid rgba(75, 85, 99, 0.5)',
                          background: 'rgba(17, 24, 39, 0.8)',
                          color: '#f3f4f6',
                          fontSize: '0.875rem'
                        }}
                      />
                      <span style={{ marginLeft: '0.5rem', color: '#cbd5e1' }}>hours</span>
                    </div>

                    {/* Pre-Rehearsal Message */}
                    <div style={{ marginBottom: '1rem' }}>
                      <label style={{ display: 'block', color: '#e2e8f0', marginBottom: '0.5rem' }}>
                        <strong>Pre-Rehearsal Reminder Message:</strong>
                      </label>
                      <textarea
                        value={directorReminder.pre_rehearsal_message}
                        onChange={(e) => setDirectorReminder(prev => ({ ...prev, pre_rehearsal_message: e.target.value }))}
                        placeholder="Message to send when notes are missing"
                        rows={3}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          borderRadius: '0.375rem',
                          border: '1px solid rgba(75, 85, 99, 0.5)',
                          background: 'rgba(17, 24, 39, 0.8)',
                          color: '#f3f4f6',
                          fontSize: '0.875rem',
                          resize: 'vertical'
                        }}
                      />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button 
                      className="updates-button"
                      onClick={saveDirectorReminder}
                      disabled={directorReminderLoading}
                      style={{ background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.8), rgba(22, 163, 74, 0.8))' }}
                    >
                      {directorReminderLoading ? 'Saving...' : 'Save Configuration'}
                    </button>
                    
                    <button 
                      className="updates-button"
                      onClick={sendTestDirectorReminder}
                      disabled={updating || !directorReminder.director_telegram_id}
                      style={{ background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.8), rgba(124, 58, 237, 0.8))' }}
                    >
                      {updating ? 'Sending...' : 'Send Test Message'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quick Reference */}
          <div className="quick-info-section">
            <h3 style={{ color: '#f1f5f9' }}>Quick Reference</h3>
            <div className="info-grid">
              <div className="info-card">
                <h4>Finding Chat IDs</h4>
                <ul>
                  <li>For groups: Add bot to group, then use "Get Updates"</li>
                  <li>For channels: Make bot admin, then use "Get Updates"</li>
                  <li>Group IDs are negative numbers</li>
                  <li>Channel IDs start with -100</li>
                </ul>
              </div>
              
              <div className="info-card">
                <h4>AI Commands</h4>
                <ul>
                  <li><strong>/start</strong> - Welcome message</li>
                  <li><strong>/help</strong> - How to use the bot</li>
                  <li><strong>/info</strong> - Choir information</li>
                  <li><strong>@DanangChoirBot</strong> - Ask anything!</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Toast Notification */}
      {toast && (
        <div className={`toast-notification toast-${toast.type}`}>
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default ManageTelegramBot;